import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  getAllBudgets,
  getDefaultBudgetId,
  getLatestBudget,
  getBudget,
  updateBudget,
  createBudget,
  deleteBudget,
  duplicateBudget,
  setDefaultBudget,
} from '../db';
import { Budget } from '../types/budget';

// Custom event name for budget data updates
import { BUDGET_DATA_UPDATED_EVENT } from '../constants/events';

// Helper function to convert string IDs to numbers
const normalizeId = (id: string | number): number => {
  return typeof id === 'string' ? parseInt(id, 10) : id;
};

// Helper function to create an event listener for data updates
const useDataUpdateListener = (
  queryKey: unknown[],
  queryClient: ReturnType<typeof useQueryClient>
) => {
  useEffect(() => {
    const handleDataUpdated = (_event: Event) => {
      queryClient.invalidateQueries({ queryKey });
    };

    window.addEventListener(BUDGET_DATA_UPDATED_EVENT, handleDataUpdated);
    return () => {
      window.removeEventListener(BUDGET_DATA_UPDATED_EVENT, handleDataUpdated);
    };
  }, [queryClient, queryKey]);
};

/**
 * Custom hook to fetch all budgets
 */
export function useAllBudgets() {
  const queryClient = useQueryClient();
  useDataUpdateListener(['budgets'], queryClient);
  return useQuery<Budget[]>({ queryKey: ['budgets'], queryFn: getAllBudgets });
}

/**
 * Custom hook to fetch the default budget ID
 */
export function useDefaultBudgetId() {
  const queryClient = useQueryClient();
  useDataUpdateListener(['defaultBudgetId'], queryClient);
  return useQuery<number | null>({ queryKey: ['defaultBudgetId'], queryFn: getDefaultBudgetId });
}

/**
 * Custom hook to fetch the default budget
 */
export function useDefaultBudget() {
  const queryClient = useQueryClient();
  useDataUpdateListener(['defaultBudget'], queryClient);
  return useQuery<Budget | null>({ queryKey: ['defaultBudget'], queryFn: getLatestBudget });
}

/**
 * Custom hook to fetch a specific budget by ID
 */
export function useBudget(id: string | number) {
  const queryClient = useQueryClient();
  const budgetId = normalizeId(id);
  useDataUpdateListener(['budget', budgetId], queryClient);
  return useQuery<Budget | null>({
    queryKey: ['budget', budgetId],
    queryFn: () => getBudget(budgetId),
  });
}

// Helper type for mutation success handling
type MutationSuccessHandler = () => void;

// Helper function to create a success handler that dispatches the update event
const createSuccessHandler = (): MutationSuccessHandler => {
  return () => {
    window.dispatchEvent(new Event(BUDGET_DATA_UPDATED_EVENT));
  };
};

/**
 * Custom hook to update a budget
 */
export function useUpdateBudget() {
  return useMutation({
    mutationFn: (budget: Budget) => {
      updateBudget(budget);
      return Promise.resolve(budget);
    },
    onSuccess: createSuccessHandler(),
  });
}

/**
 * Custom hook to create a new budget
 */
export function useCreateBudget() {
  const queryClient = useQueryClient();
  return useMutation<number, Error, Budget>({
    mutationFn: (budget: Budget) => Promise.resolve(createBudget(budget)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
}

/**
 * Custom hook to delete a budget
 */
export function useDeleteBudget() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (budgetId: number) => Promise.resolve(deleteBudget(budgetId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
}

/**
 * Custom hook to duplicate a budget
 */
export function useDuplicateBudget() {
  const queryClient = useQueryClient();
  return useMutation<number, Error, { budgetId: number; newName: string }>({
    mutationFn: ({ budgetId, newName }) => Promise.resolve(duplicateBudget(budgetId, newName)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
}

/**
 * Custom hook to set the default budget
 */
export function useSetDefaultBudget() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (budgetId: number) => Promise.resolve(setDefaultBudget(budgetId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['defaultBudgetId'] });
      queryClient.invalidateQueries({ queryKey: ['defaultBudget'] });
    },
  });
}
